/**
 * Button — Botón estándar con variantes, tamaños y estado de carga.
 *
 * Centraliza el patrón repetido de botones primary/secondary/danger en toda la app.
 * Para botones de solo icono, usar `<IconButton>`.
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'

type Variant = 'primary' | 'secondary' | 'danger' | 'success' | 'ghost' | 'warning' | 'info' | 'ghost-warning' | 'ghost-info'
type Size = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  isPending?: boolean
  pendingLabel?: string
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  fullWidth?: boolean
}

/**
 * Las variantes SÓLIDAS (danger, success, warning, info) usan `*-fill`, no
 * `bg-danger` y compañía: esos valen el 400 en oscuro, que sirve para texto e
 * íconos pero deja al texto blanco de un botón en 2,77:1 o menos.
 *
 * `*-fill` vale lo mismo en los dos temas, y success y warning un peldaño más
 * oscuros que el resto: con el 600 el blanco no llegaba a AA (3,77:1 y 3,19:1).
 * Los cuatro quedan sobre 4,5:1, y `estados.test.ts` lo vigila.
 *
 * El deshabilitado atenúa el botón entero (`opacity-60`, igual que primary):
 * un token es un color completo y no admite el modificador `/60`.
 *
 * Las variantes `ghost-*` usan tokens de texto: ahí el color es el TEXTO.
 */
const variantCls: Record<Variant, string> = {
  // El degradado de marca, con el color de texto que le corresponde a cada
  // tema. `text-white` fijo fallaba AA en oscuro, donde el primario es la
  // menta: daba 2,19:1 contra los 8,20:1 de la tinta.
  primary: 'bg-primary-gradient hover:brightness-110 disabled:opacity-60 text-on-primary',
  secondary: 'bg-surface hover:bg-soft text-content-muted border border-divider',
  danger: 'bg-danger-fill hover:bg-danger-fill-hover disabled:opacity-60 text-white',
  success: 'bg-success-fill hover:bg-success-fill-hover disabled:opacity-60 text-white',
  ghost: 'bg-transparent hover:bg-soft text-content',
  warning: 'bg-warning-fill hover:bg-warning-fill-hover disabled:opacity-60 text-white',
  info: 'bg-info-fill hover:bg-info-fill-hover disabled:opacity-60 text-white',
  'ghost-warning': 'bg-transparent hover:bg-warning-surface text-warning-content-soft',
  'ghost-info': 'bg-transparent hover:bg-info-surface text-info-content-soft',
}

const sizeCls: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-xs rounded-md gap-1.5',
  md: 'px-4 py-2 text-sm rounded-lg gap-2',
  lg: 'px-5 py-2.5 text-sm rounded-lg gap-2',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    variant = 'primary',
    size = 'md',
    isPending = false,
    pendingLabel,
    leftIcon,
    rightIcon,
    fullWidth = false,
    className = '',
    type = 'button',
    disabled,
    children,
    ...rest
  }, ref) => {
    const spinnerSize = size === 'sm' ? 12 : size === 'lg' ? 16 : 14
    const widthCls = fullWidth ? 'w-full' : ''
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || isPending}
        className={`inline-flex items-center justify-center font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed cursor-pointer ${sizeCls[size]} ${variantCls[variant]} ${widthCls} ${className}`}
        {...rest}
      >
        {isPending ? (
          <>
            <Loader2 size={spinnerSize} className="animate-spin" />
            {pendingLabel ?? children}
          </>
        ) : (
          <>
            {leftIcon}
            {children}
            {rightIcon}
          </>
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'
